import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination
} from '@mui/material';
import { Save, ArrowLeft, Plus, Delete as DeleteIcon, Edit as EditIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { toast } from 'react-toastify';

export default function FeeConfigForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [tipiRapporto, setTipiRapporto] = useState([]);
  
  // Config State
  const [formData, setFormData] = useState({
    name: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: ''
  });

  // Rules State
  const [rules, setRules] = useState([]);
  const [filterTipoId, setFilterTipoId] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Rule Dialog State
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState(null); // null = new, number = index in rules array
  const [ruleFormData, setRuleFormData] = useState({
    tipo_rapporto_id: '',
    min_debt_amount: '0',
    max_debt_amount: '',
    fixed_fee: '0',
    variable_fee_percent: '0',
    min_fee_amount: '0'
  });

  useEffect(() => {
    fetchTipiRapporto();
    if (isEditMode) {
      fetchConfig();
    }
  }, [id]);

  const fetchTipiRapporto = async () => {
    try {
      const response = await api.get('/tipi-rapporto');
      setTipiRapporto(response.data);
    } catch (err) {
      console.error('Error fetching tipi rapporto:', err);
      toast.error('Errore nel caricamento dei tipi rapporto');
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/fee-configs/${id}`);
      
      const config = response.data;
      setFormData({
        name: config.name,
        valid_from: config.valid_from ? config.valid_from.split('T')[0] : '',
        valid_to: config.valid_to ? config.valid_to.split('T')[0] : ''
      });
      
      // Ensure numeric values are strings for inputs
      const formattedRules = (config.rules || []).map(r => ({
        ...r,
        tipo_rapporto_id: r.tipo_rapporto_id || '',
        min_debt_amount: parseFloat(r.min_debt_amount).toString(),
        max_debt_amount: r.max_debt_amount ? parseFloat(r.max_debt_amount).toString() : '',
        fixed_fee: parseFloat(r.fixed_fee).toString(),
        variable_fee_percent: parseFloat(r.variable_fee_percent).toString(),
        min_fee_amount: parseFloat(r.min_fee_amount).toString()
      }));
      
      setRules(formattedRules);
    } catch (err) {
      console.error('Error fetching fee config:', err);
      toast.error('Errore nel caricamento del tabellario');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveConfig = async () => {
    if (!formData.name || !formData.valid_from) {
      toast.warning('Nome e Data Inizio Validità sono obbligatori');
      return;
    }

    try {
      const payload = {
        ...formData,
        rules: rules.map(r => ({
          ...r,
          // Convert empty strings to null for optional fields
          max_debt_amount: r.max_debt_amount === '' ? null : r.max_debt_amount,
          tipo_rapporto_id: r.tipo_rapporto_id === '' ? null : r.tipo_rapporto_id
        }))
      };

      if (isEditMode) {
        await api.put(`/fee-configs/${id}`, payload);
        toast.success('Tabellario aggiornato con successo');
      } else {
        await api.post('/fee-configs', payload);
        toast.success('Tabellario creato con successo');
      }
      navigate('/fee-configs');
    } catch (err) {
      console.error('Error saving fee config:', err);
      toast.error('Errore nel salvataggio');
    }
  };

  // Rule Dialog Handlers
  const handleOpenRuleDialog = (index = null) => {
    if (index !== null) {
      setEditingRuleIndex(index);
      setRuleFormData({ ...rules[index] });
    } else {
      setEditingRuleIndex(null);
      setRuleFormData({
        tipo_rapporto_id: '',
        min_debt_amount: '0',
        max_debt_amount: '',
        fixed_fee: '0',
        variable_fee_percent: '0',
        min_fee_amount: '0'
      });
    }
    setOpenRuleDialog(true);
  };

  const handleRuleChange = (e) => {
    const { name, value } = e.target;
    setRuleFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveRule = () => {
    // Basic validation
    if (parseFloat(ruleFormData.min_debt_amount) < 0) {
      toast.warning('Importo debito minimo non valido');
      return;
    }

    const newRule = { ...ruleFormData };
    
    // Add label for display purposes (find it from tipiRapporto)
    if (newRule.tipo_rapporto_id) {
      const type = tipiRapporto.find(t => t.id === parseInt(newRule.tipo_rapporto_id));
      newRule.tipo_rapporto_label = type ? type.label : '';
    } else {
      newRule.tipo_rapporto_label = 'Tutti';
    }

    if (editingRuleIndex !== null) {
      // Update existing
      const updatedRules = [...rules];
      updatedRules[editingRuleIndex] = newRule;
      setRules(updatedRules);
    } else {
      // Add new
      setRules([...rules, newRule]);
    }
    setOpenRuleDialog(false);
  };

  const handleDeleteRule = (index) => {
    const updatedRules = rules.filter((_, i) => i !== index);
    setRules(updatedRules);
  };

  const filteredRules = rules.filter(rule => {
    if (filterTipoId === 'ALL') return true;
    if (filterTipoId === '') return !rule.tipo_rapporto_id; // Handle Generic/Empty
    return rule.tipo_rapporto_id == filterTipoId; // Loose equality for string/number match
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRules = filteredRules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fee-configs')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <Typography variant="h4" className="font-semibold text-gray-800 dark:text-gray-100">
              {isEditMode ? 'Modifica Tabellario' : 'Nuovo Tabellario'}
            </Typography>
          </div>
        </div>
        <Button onClick={handleSaveConfig} className="gap-2">
          <Save className="h-4 w-4" />
          Salva Tabellario
        </Button>
      </div>

      {/* Main Config Form */}
      <Card className="p-6 border shadow-sm">
        <Typography variant="h6" className="mb-4 text-gray-700">Dettagli Generali</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome Tabellario"
              name="name"
              value={formData.name}
              onChange={handleConfigChange}
              required
              placeholder="es. Tabellario Standard 2024"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Valido Dal"
              name="valid_from"
              type="date"
              value={formData.valid_from}
              onChange={handleConfigChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Valido Al (Opzionale)"
              name="valid_to"
              type="date"
              value={formData.valid_to}
              onChange={handleConfigChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Card>

      {/* Rules Section */}
      <Card className="border shadow-sm overflow-hidden">
        <div className="p-4 bg-gray-50 dark:bg-gray-800 border-b flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4 flex-1 w-full sm:w-auto">
            <Typography variant="h6" className="text-gray-700 dark:text-gray-200 whitespace-nowrap">Regole di Calcolo</Typography>
            <div className="w-full sm:max-w-xs">
              <Autocomplete
                size="small"
                options={[{ id: 'ALL', label: 'Tutti i tipi' }, { id: '', label: 'Generico (Tutti)' }, ...tipiRapporto]}
                getOptionLabel={(option) => option.label + (option.code ? ` (${option.code})` : '')}
                value={
                  filterTipoId === 'ALL' 
                    ? { id: 'ALL', label: 'Tutti i tipi' }
                    : filterTipoId === '' 
                      ? { id: '', label: 'Generico (Tutti)' }
                      : tipiRapporto.find(t => t.id == filterTipoId) || null
                }
                onChange={(event, newValue) => {
                  setFilterTipoId(newValue ? newValue.id : 'ALL');
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Filtra per Tipo" 
                    fullWidth 
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id == value.id}
                disableClearable
              />
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => handleOpenRuleDialog()} className="gap-2">
            <Plus className="h-4 w-4" />
            Aggiungi Regola
          </Button>
        </div>
        
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell>Tipo Rapporto</TableCell>
                <TableCell>Da Importo (€)</TableCell>
                <TableCell>A Importo (€)</TableCell>
                <TableCell>Fisso (€)</TableCell>
                <TableCell>Variabile (%)</TableCell>
                <TableCell>Minimo (€)</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" className="py-8 text-gray-500">
                    {rules.length === 0 ? 'Nessuna regola definita. Aggiungi la prima regola.' : 'Nessuna regola trovata con i filtri correnti.'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRules.map((rule) => {
                  const originalIndex = rules.indexOf(rule);
                  return (
                    <TableRow key={originalIndex} hover>
                      <TableCell className="font-medium">
                        {rule.tipo_rapporto_label || (
                          rule.tipo_rapporto_id 
                            ? tipiRapporto.find(t => t.id === parseInt(rule.tipo_rapporto_id))?.label 
                            : 'Tutti'
                        )}
                      </TableCell>
                      <TableCell>{parseFloat(rule.min_debt_amount).toFixed(2)}</TableCell>
                      <TableCell>{rule.max_debt_amount ? parseFloat(rule.max_debt_amount).toFixed(2) : '∞'}</TableCell>
                      <TableCell>{parseFloat(rule.fixed_fee).toFixed(2)}</TableCell>
                      <TableCell>{parseFloat(rule.variable_fee_percent).toFixed(2)}%</TableCell>
                      <TableCell>{parseFloat(rule.min_fee_amount).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <div className="flex justify-end gap-2">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenRuleDialog(originalIndex)}
                            color="primary"
                          >
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteRule(originalIndex)}
                            color="error"
                          >
                            <DeleteIcon size={18} />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`}
        />
      </Card>

      {/* Rule Dialog */}
      <Dialog open={openRuleDialog} onClose={() => setOpenRuleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRuleIndex !== null ? 'Modifica Regola' : 'Nuova Regola'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={[{ id: '', label: 'Tutti (Generico)', code: '' }, ...tipiRapporto]}
                getOptionLabel={(option) => option.label + (option.code ? ` (${option.code})` : '')}
                value={
                  ruleFormData.tipo_rapporto_id 
                    ? tipiRapporto.find(t => t.id === parseInt(ruleFormData.tipo_rapporto_id)) || { id: '', label: 'Tutti (Generico)', code: '' }
                    : { id: '', label: 'Tutti (Generico)', code: '' }
                }
                onChange={(event, newValue) => {
                  setRuleFormData(prev => ({
                    ...prev,
                    tipo_rapporto_id: newValue ? newValue.id : ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Tipo Rapporto" 
                    fullWidth 
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id == value.id}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Da Importo Debito"
                name="min_debt_amount"
                type="number"
                value={ruleFormData.min_debt_amount}
                onChange={handleRuleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="A Importo Debito (Lascia vuoto per infinito)"
                name="max_debt_amount"
                type="number"
                value={ruleFormData.max_debt_amount}
                onChange={handleRuleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Compenso Fisso"
                name="fixed_fee"
                type="number"
                value={ruleFormData.fixed_fee}
                onChange={handleRuleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Variabile su Recuperato"
                name="variable_fee_percent"
                type="number"
                value={ruleFormData.variable_fee_percent}
                onChange={handleRuleChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Compenso Minimo"
                name="min_fee_amount"
                type="number"
                value={ruleFormData.min_fee_amount}
                onChange={handleRuleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setOpenRuleDialog(false)}>Annulla</Button>
          <Button onClick={handleSaveRule}>Salva Regola</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
